import { useState } from 'react'
import { autoDraftLesson, errorMessage } from '../../../services/admin'
import { LEVELS, LEVEL_FA } from '../constants'

// ─── Auto-draft: create a lesson from an uploaded video/voice file ────────────

/**
 * Upload-only counterpart to CreateForm: a title, the level and order, and
 * one file (video or a stand-alone voice recording) instead of a typed
 * sentence. The backend recognises the speech as the lesson's one sentence
 * and takes the file's sound track as its reference audio
 * (admin-lessons/auto-draft); nothing is translated. The returned lesson
 * comes back with processingStatus "processing"; ProcessingLessonCard takes
 * it from there, and the admin checks the recognised text before confirming.
 */
export function AutoDraftForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({ titleEn: '', level: 'beginner', displayOrder: 0 })
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  async function submit(e) {
    e.preventDefault()
    if (!file) { setErr('یک فایل ویدیو یا صوتی انتخاب کنید.'); return }
    setBusy(true); setErr('')
    try {
      const lesson = await autoDraftLesson({
        titleEn: form.titleEn.trim(),
        level: form.level,
        displayOrder: Number(form.displayOrder),
        file,
      })
      onCreated(lesson)
    } catch (ex) { setErr(errorMessage(ex)) }
    finally { setBusy(false) }
  }

  const FL = 'mb-1 block text-xs font-medium text-slate-600'
  const FI = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none'

  return (
    <form onSubmit={submit} className="space-y-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div>
        <h2 className="text-lg font-bold text-slate-800">درس جدید از ویدیو یا صدا</h2>
        <p className="mt-1 text-xs text-slate-500">
          فقط فایل را بارگذاری کنید؛ جملهٔ آن خودکار تشخیص داده می‌شود و پیش از انتشار
          می‌توانید متن را بازبینی و اصلاح کنید.
        </p>
      </div>

      <label className="block">
        <span className={FL}>عنوان درس *</span>
        <input required value={form.titleEn} className={FI} placeholder="Allergic" dir="ltr"
          onChange={e => set('titleEn', e.target.value)} />
      </label>

      <label className="block">
        <span className={FL}>فایل ویدیو یا صدا *</span>
        <input required type="file" accept="video/*,audio/*" className={FI}
          onChange={e => setFile(e.target.files?.[0] ?? null)} />
      </label>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block">
          <span className={FL}>سطح *</span>
          <select value={form.level} onChange={e => set('level', e.target.value)} className={FI}>
            {LEVELS.map(l => <option key={l} value={l}>{LEVEL_FA[l]}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={FL}>ترتیب نمایش</span>
          <input type="number" min="0" step="1" value={form.displayOrder} className={FI}
            onChange={e => set('displayOrder', e.target.value)} />
        </label>
      </div>

      {err && <p className="text-sm text-red-600">{err}</p>}

      <div className="flex gap-3 pt-1">
        <button type="submit" disabled={busy}
          className="rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
          {busy ? 'در حال بارگذاری…' : 'بارگذاری و پردازش'}
        </button>
        <button type="button" onClick={onCancel}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
          انصراف
        </button>
      </div>
    </form>
  )
}
